import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import { Location } from 'history'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { getTotalAvailableCreditV2 } from 'src/app/Home/redux/home.actions'
import {
  selectTotalAvailableCreditCentsCADFromState,
  selectTotalAvailableCreditCentsUSDFromState,
} from 'src/app/Home/redux/home.selectors'
import Box from 'src/components/Box/Box'
import MetricsLink from 'src/components/HomepageTopMetricsLink.tsx/HomepageTopMetricsLink'
import HoverTooltip from 'src/components/HoverTooltip/HoverTooltip'
import SvgAlertCircledFilled from 'src/components/Svgs/SvgAlertCircledFilled'
import TestId from 'src/components/TestId/TestId'
import Tooltip from 'src/components/Tooltip/Tooltip'
import { ExtendCurrency } from 'src/enums'
import { useBackgroundDispatch } from 'src/hooks'
import { useCurrencyFormatter } from 'src/localization/Crncy'
import { useTranslate } from 'src/localization/Trns'
import { UserFeature } from 'src/models/user.models'
import {
  accountsAccountsUrl,
  budgetsAllBudgetsUrl,
  registerAccountUrl,
  virtualCardsAllCardsUrl,
} from 'src/navigation/urls'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import { LocationState } from 'src/redux/createRootReducer'
import { toDollars } from 'src/utils/currency.utils'
import { css } from 'styled-components'

export interface HomePageTopMetricsProps {
  activeVirtualCards: number
  activeVirtualCardsLoaded: boolean
  activeBudgets: number
  activeBudgetsLoaded: boolean
  activeAccounts: number
  activeAccountsLoaded: boolean
  actionRequired: boolean
}

const HomepageTopMetrics: React.FC<HomePageTopMetricsProps> = (props) => {
  const translate = useTranslate()

  const currencyFormatter = useCurrencyFormatter()
  const totalAvailableCreditCentsUSD = useSelector(
    selectTotalAvailableCreditCentsUSDFromState,
  )
  const totalAvailableCreditCentsCAD = useSelector(
    selectTotalAvailableCreditCentsCADFromState,
  )
  const { REGISTER_ACCOUNT: canRegisterAccount } =
    useQueryUserFeatures([UserFeature.REGISTER_ACCOUNT]).data || {}

  const { analytics } = useAnalyticsContext()

  const dispatch = useBackgroundDispatch()

  const [showAccountsTooltip, setShowAccountsTooltip] = useState<boolean>(false)

  useEffect(() => {
    dispatch(getTotalAvailableCreditV2())
  }, [])

  const accountsLink: string | Location<LocationState> = !showAccountsTooltip
    ? props.activeAccounts > 0
      ? {
          pathname: accountsAccountsUrl(),
          search: `?displayStatuses=ACTIVE`,
          state: {},
          hash: '',
        }
      : canRegisterAccount
        ? registerAccountUrl()
        : ''
    : ''

  return (
    <Box
      css={css`
        border-top: 3px solid transparent;
        background:
          linear-gradient(${styleConstants.colors.white} 0 0) padding-box,
          linear-gradient(
              to right,
              ${themeConstants.colors.highlight},
              ${themeConstants.colors.cta}
            )
            border-box;
      `}
      noPadding
    >
      {/* TODO: adjust padding values */}
      <TestId testId={'topMetrics'}>
        <div
          css={css`
            display: flex;
          `}
        >
          <Link
            css={css`
              padding: 40px 16px 40px 40px;
              flex-grow: 1;
              cursor: pointer;
              &:hover {
                .link {
                  ${getFontCss(
                    20,
                    themeConstants.colors.cta_dark,
                    styleConstants.fontFamilies.medium,
                  )};
                }
              }
            `}
            to={{
              pathname: virtualCardsAllCardsUrl(),
              search: `?statuses=ACTIVE`,
              state: {},
              hash: '',
            }}
            onClick={(): void => {
              analytics?.track('top metrics - TAC')
            }}
            onKeyDown={(): void => {
              analytics?.track('top metrics - TAC')
            }}
            role={'link'}
            tabIndex={0}
          >
            <div
              css={css`
                ${getFontCss(
                  14,
                  styleConstants.colors.gray_darkest,
                  styleConstants.fontFamilies.regular,
                )};
                margin-bottom: 8px;
                display: flex;
                align-items: center;
              `}
            >
              {translate('Total Available Credit')}
              <TestId testId={'tacTooltip'}>
                <Tooltip
                  css={css`
                    height: 19px;
                  `}
                  openLeft={false}
                  transformScale={0.9}
                  transformOrigin="top"
                  onHover={(): void => {
                    analytics?.track('home page - TAC tooltip')
                  }}
                >
                  <TestId testId={'tacTooltipText'}>
                    <div
                      css={css`
                        display: inline-block;
                      `}
                    >
                      <span
                        css={css`
                          font-weight: bold;
                        `}
                      >
                        {translate('Total Available Credit')}
                      </span>{' '}
                      <span>
                        {translate(
                          'is the amount left to be spent on virtual cards within this organization.',
                        )}
                      </span>
                    </div>
                  </TestId>
                </Tooltip>
              </TestId>
            </div>
            <div
              className="link"
              css={css`
                ${getFontCss(
                  20,
                  themeConstants.colors.highlight,
                  styleConstants.fontFamilies.medium,
                )};
              `}
            >
              <TestId testId={'topMetricsTotalCredit'}>
                {/* if multiple currencies detected */}
                {(totalAvailableCreditCentsUSD !== null ||
                  totalAvailableCreditCentsCAD !== null) && (
                  <div
                    css={css`
                      display: flex;
                      flex-direction: column;
                      gap: 8px;
                    `}
                  >
                    {totalAvailableCreditCentsUSD !== undefined &&
                      totalAvailableCreditCentsUSD !== null && (
                        <div id="totalAvailableCreditCentsUSD">
                          {/* TODO: figure out why Crncy component returns 'US$XX.XX USD' when locale is set to en-ca */}
                          {currencyFormatter({
                            amount: toDollars(
                              totalAvailableCreditCentsUSD || 0,
                            ),
                          })}
                          {totalAvailableCreditCentsCAD !== undefined &&
                            totalAvailableCreditCentsCAD !== null && (
                              <span
                                css={css`
                                  font-size: 12px;
                                `}
                              >
                                {` ${ExtendCurrency.Usd}`}
                              </span>
                            )}
                        </div>
                      )}
                    {totalAvailableCreditCentsCAD !== undefined &&
                      totalAvailableCreditCentsCAD !== null && (
                        <div id="totalAvailableCreditCentsCAD">
                          {currencyFormatter({
                            amount: toDollars(
                              totalAvailableCreditCentsCAD || 0,
                            ),
                          })}
                          {totalAvailableCreditCentsUSD !== undefined &&
                            totalAvailableCreditCentsUSD !== null && (
                              <span
                                css={css`
                                  font-size: 12px;
                                `}
                              >
                                {` ${ExtendCurrency.Cad}`}
                              </span>
                            )}
                        </div>
                      )}
                  </div>
                )}
              </TestId>
            </div>
          </Link>
          <MetricsLink
            pathname={virtualCardsAllCardsUrl()}
            search={`?statuses=ACTIVE`}
            activeMetricsLoaded={props.activeVirtualCardsLoaded}
            metricName="Active Virtual Cards"
            metricAnalytics="top metrics - virtual cards"
            metricTestId="topMetricsVirtualCard"
            metric={props.activeVirtualCards}
          />
          <MetricsLink
            pathname={budgetsAllBudgetsUrl()}
            search={`?statuses=ACTIVE`}
            activeMetricsLoaded={props.activeBudgetsLoaded}
            metricName="Active Budgets"
            metricAnalytics="top metrics - budgets"
            metricTestId="topMetricsBudgets"
            metric={props.activeBudgets}
          />
          <MetricsLink
            linkTarget={accountsLink}
            activeMetricsLoaded={props.activeAccountsLoaded}
            metricName="Active Accounts"
            metricAnalytics="top metrics - accounts"
            metricTestId="topMetricsAccounts"
            metric={props.activeAccounts}
            bottomPadding="8px"
          >
            {props.actionRequired && (
              <Link
                css={css`
                  position: relative;
                  cursor: pointer;
                  &:hover {
                    text-decoration: underline;
                  }
                `}
                to={{
                  pathname: accountsAccountsUrl(),
                  search: `?displayStatuses=PENDING`,
                  state: {},
                  hash: '',
                }}
                onFocus={(): void => setShowAccountsTooltip(true)}
                role="link"
                tabIndex={0}
                onMouseEnter={(): void => setShowAccountsTooltip(true)}
                onMouseLeave={(): void => setShowAccountsTooltip(false)}
              >
                <TestId testId={'topMetricsActionRequired'}>
                  <div
                    css={css`
                      margin-top: 8px;
                      ${getFontCss(
                        12,
                        styleConstants.colors.red,
                        styleConstants.fontFamilies.regular,
                      )};
                      display: flex;
                      align-items: center;
                    `}
                  >
                    <SvgAlertCircledFilled
                      css={css`
                        transform: scale(0.7);
                        transform-origin: left;
                      `}
                    />
                    <span
                      css={css`
                        left: -4px;
                        position: relative;
                      `}
                    >
                      {translate('Action Required')}
                    </span>
                  </div>
                </TestId>
                <HoverTooltip
                  show={showAccountsTooltip}
                  wrapText
                  tooltipText={translate(
                    'One or more of your accounts requires verification to complete registration.',
                  )}
                />
              </Link>
            )}
          </MetricsLink>
        </div>
      </TestId>
    </Box>
  )
}

export default HomepageTopMetrics
